import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CalendarDays, MapPin, Anchor } from "lucide-react";
import { SAILING_EVENTS, POWER_BOAT_RACES } from "../lib/events";

export default function Events() {
  const navigate = useNavigate();

  const bookForEvent = (experience) => {
    navigate("/book", { state: { experience } });
  };

  return (
    <div data-testid="events-page" className="bg-sand">
      {/* HERO */}
      <section className="pt-28 pb-14 max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="overline mb-5">Bermuda Calendar</div>
        <h1 className="font-serif-display font-light text-5xl md:text-6xl lg:text-7xl text-navy leading-[1.02] max-w-4xl">
          The season's best <em className="italic">views</em> are from the water.
        </h1>
        <p className="mt-6 text-[color:var(--am-muted)] max-w-2xl leading-relaxed">
          Regattas, world-class powerboat racing, and the beloved traditions that make
          Bermuda Bermuda. Charter Anchor Management for a front-row seat to any of the
          below — our schedule books up fastest on these dates.
        </p>
      </section>

      {/* SAILING & RACING EVENTS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline mb-2">01 · Sailing &amp; Racing</div>
            <h2 className="font-serif-display text-4xl lg:text-5xl text-navy leading-tight">
              Marquee events this season
            </h2>
          </div>
          <Anchor size={28} strokeWidth={1.5} className="text-navy/40 hidden sm:block" />
        </div>

        <div className="grid md:grid-cols-2 gap-6" data-testid="sailing-events-grid">
          {SAILING_EVENTS.map((e, i) => (
            <article
              key={e.id}
              className="group bg-white border border-[color:var(--am-line)] p-8 flex flex-col hover:border-navy transition-colors"
              data-testid={`event-${e.id}`}
            >
              <div className="flex items-center gap-2 text-[0.7rem] tracking-[0.22em] uppercase text-[color:var(--am-muted)] mb-4">
                <CalendarDays size={13} />
                <span>{e.dateLabel}</span>
              </div>
              <div className="overline mb-2">0{i + 1}</div>
              <h3 className="font-serif-display text-3xl text-navy leading-tight mb-3">
                {e.name}
              </h3>
              <p className="italic text-[color:var(--am-muted)] text-sm mb-4">
                {e.tagline}
              </p>
              <p className="text-sm text-navy/80 leading-relaxed mb-6 flex-1">
                {e.description}
              </p>
              <div className="pt-5 border-t border-[color:var(--am-line)] flex items-center justify-between">
                <span className="text-[0.7rem] tracking-[0.22em] uppercase text-[color:var(--am-muted)]">
                  Recommended · {e.experience}
                </span>
                <button
                  type="button"
                  onClick={() => bookForEvent(e.experience)}
                  className="text-[0.72rem] tracking-[0.22em] uppercase text-navy hover:opacity-60 inline-flex items-center gap-1.5"
                  data-testid={`event-book-${e.id}`}
                >
                  Book <ArrowRight size={13} />
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* POWER BOAT RACE SCHEDULE */}
      <section className="bg-navy text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="overline text-pink-soft mb-2">02 · Power Boat Racing</div>
              <h2 className="font-serif-display text-4xl lg:text-5xl leading-tight">
                2026 Power Boat Schedule
              </h2>
              <p className="mt-4 text-white/70 max-w-xl leading-relaxed">
                Every race below runs at <strong className="text-white">Ferry Reach</strong> —
                one of the best natural amphitheatres on the island. Drop anchor nearby and
                watch the speed, spray, and roaring engines up close.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-px bg-white/10" data-testid="powerboat-grid">
            {POWER_BOAT_RACES.map((r) => (
              <div
                key={r.id}
                className="bg-navy p-6 flex flex-col gap-2"
                data-testid={`powerboat-${r.id}`}
              >
                <div className="flex items-center gap-2 text-[0.68rem] tracking-[0.22em] uppercase text-pink-soft">
                  <CalendarDays size={12} />
                  {r.dateLabel}
                </div>
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <MapPin size={13} className="text-white/50" />
                  {r.location}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <button
              type="button"
              onClick={() => bookForEvent("Daytime Cruise")}
              className="inline-flex items-center gap-2 bg-pink-soft text-navy px-6 py-3 text-[0.72rem] tracking-[0.22em] uppercase hover:bg-white transition-colors"
              data-testid="book-powerboat-charter"
            >
              Charter for a Race Day <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1100px] mx-auto px-6 lg:px-12 py-24 text-center">
        <div className="overline mb-4">Have a private event?</div>
        <h2 className="font-serif-display text-4xl lg:text-5xl text-navy leading-tight">
          Cup Match. Christmas Boat Parade. Your anniversary.
        </h2>
        <p className="mt-5 text-[color:var(--am-muted)] max-w-xl mx-auto leading-relaxed">
          We also charter for birthdays, anniversaries, corporate off-sites, and any
          Bermudian tradition you want to see from the water. Tell us the date and we'll
          build the day around you.
        </p>
        <Link
          to="/book"
          className="btn-primary mt-10 inline-flex"
          data-testid="events-cta-book"
        >
          Plan your charter <ArrowRight size={14} />
        </Link>
      </section>
    </div>
  );
}
